import useUploadCustomerProposal from '~/hooks/useUploadCustomerProposal';
import { useProjectContext } from '~/hooks';
import { useConversationListQuery } from '~/requests/conversations/useConversationListQuery';
import { useDownloadMessageFileQuery } from '~/requests/conversations/useDownloadMessageFileQuery';
import { usePermissions } from '~/requests/permissions/usePermissions';
import { Button } from '~/components';
import { ButtonProps } from '~/components/Button';
import { PricingEstimateProposalTypeDisplay } from '~/constants';
import { Conversation } from '~/types/conversations/Conversation';
import useArchiveToastCheck from '~/hooks/archive/useArchiveToastCheck';

interface PricingEstimateProposalButtonProps {
  isPriceEstimateStale: boolean;
  onClick: () => void;
  proposalType: string;
}

export default function PricingEstimateProposalButton({
  isPriceEstimateStale,
  onClick,
  proposalType,
}: PricingEstimateProposalButtonProps) {
  const { project } = useProjectContext();
  const { permissions } = usePermissions();
  const { data: conversations } = useConversationListQuery();
  const supportConversationId = conversations?.find((conversation: Conversation) => conversation.type === 'SUPPORT' && !!conversation.installerId)?.id as string;
  
  const { getInputProps, open, updateProposalRequestStatus } = useUploadCustomerProposal(project?.id, supportConversationId);
  const { refetch: downloadProposalFile, status: downloadProposalFileStatus } = useDownloadMessageFileQuery({
    conversationId: supportConversationId,
    messageId: project?.customerProposal?.messageId as string
  });

  const proposalText = PricingEstimateProposalTypeDisplay[proposalType];
  const handleClick = useArchiveToastCheck(onClick);
  const handleUpload = useArchiveToastCheck(open);
  const handleDownload = useArchiveToastCheck(downloadProposalFile);

  const buttonProps: ButtonProps = (() => {
    if (isPriceEstimateStale && !(project?.customerProposal?.requestedAt && !project?.customerProposal?.messageId)) {      
      return {
        content: 'Please update pricing estimate',
        disabled: true
      };
    }
    
    if (!project?.customerProposal) {
      return {
        content: `Request customer ${proposalText} proposal`,
        disabled: !project?.autopricingResult,
        onClick: () => handleClick()
      };
    } 
    
    // Admins also use this button to upload proposals 
    // For non-admins, this acts as a badge to indicate that the proposal has been requested
    if (project?.customerProposal?.requestedAt && !project?.customerProposal?.messageId) {
      return {
        content: `Customer ${proposalText} proposal requested`,
        loading: updateProposalRequestStatus === 'loading',
        disabled: updateProposalRequestStatus === 'loading' || !permissions.isAdmin,    // Disabled for non-admins once the request is made
        onClick: () => handleUpload()
      };
    }
    
    if (project?.customerProposal?.uploadedAt && project?.customerProposal?.messageId) {
      return {
        content: `View customer ${proposalText} proposal`,
        loading: downloadProposalFileStatus === 'loading',
        disabled: downloadProposalFileStatus === 'loading',
        onClick: () => handleDownload()
      };
    }

    return {};
  })();

  return (
    <>
      {project?.customerProposal?.proposalType && !project?.customerProposal?.uploadedAt && (
        <input {...getInputProps()} />
      )}
      <Button 
        primary 
        outlined
        size="small"
        {...buttonProps}
      />
    </>
  );
}